import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { etichettePattaBloccata } from '../../utils/chiave-rifiuto-patta';
import { Eye, List, MessageSquare } from 'lucide-react';
import { useArenaStore } from '../../store/arena-store';
import { useChatStore } from '../../store/chat-store';
import { useGameStore } from '../../store/game-store';
import { useAuthStore } from '../../store/auth-store';
import type { Move } from '@draughts/shared';
import { Color, GameStatus, resolveTimeControl } from '@draughts/shared';
import { getRatingForGame } from '../../utils/rating';
import GameScreen from '../game/GameScreen';
import type { MatchFoundData, MobilePanel } from '../game/GameScreen';
import { useGuardiaSincronia } from '../../hooks/useGuardiaSincronia';
import { useMultiplayerStore } from '../../store/multiplayer-store';
import GameSidebarTabs from '../game/GameSidebarTabs';
import FullscreenPanel from '../game/FullscreenPanel';
import { useIsMobile } from '../../hooks/useIsMobile';
import ClassificaArenaInPartita from './ClassificaArenaInPartita';
import MoveList from '../game/MoveList';
import ChatPanel from '../online/ChatPanel';
import SpectatorsPanel from '../game/SpectatorsPanel';
import { useGameSpectators } from '../../store/spectators-store';
import ArenaGameEndOverlay from './ArenaGameEndOverlay';
import { endReasonText } from '../../utils/end-reason-text';
import ArenaHeaderCells from './ArenaHeaderCells';
import ArenaBoostButton, { ArenaBoostBadge, useCanBoost } from './ArenaBoostButton';
import { useArenaPartita } from './useArenaPartita';
import { useArenaScaduta } from '../../hooks/useArenaScaduta';
import DrawnOpeningBanner from '../online/DrawnOpeningBanner';
import AvvisoErrore from '../online/AvvisoErrore';
import './Arena.css';
import { log } from '../../utils/logger';
import { DRAW_DECLINE_NOTICE_MS } from '../../constants/timing';

interface ArenaGameProps {
  onBackToLobby: () => void;
}

export default function ArenaGame({ onBackToLobby }: ArenaGameProps) {
  const { t } = useTranslation();

  /*
   * Un selettore per campo, non `useArenaStore()`: destrutturare l'intero store
   * significa sottoscriversi a ogni sua modifica, e in arena la classifica di
   * TUTTI arriva ogni due secondi. Ognuna di quelle righe ridisegnava questa
   * scacchiera, il suo orologio, la lista mosse e la chat — di una partita che
   * non era cambiata di una virgola. Stesso modello di OnlineGame.tsx.
   */
  const status = useArenaStore((s) => s.status);
  const boardState = useArenaStore((s) => s.boardState);
  const playerColor = useArenaStore((s) => s.playerColor);
  const opponent = useArenaStore((s) => s.opponent);
  const gameResult = useArenaStore((s) => s.gameResult);
  const gameEndReason = useArenaStore((s) => s.gameEndReason);
  /*
   * L'errore di partita e il modo di chiuderlo. `AvvisoErrore` era montato SOLO in
   * `OnlineGame` (grep in tutto `src`: la definizione e quell'unico montaggio), quindi
   * qui `error` era scritto e mai letto: dal rifiuto di una mossa al riavvio del
   * server, che arriva a tutta la stanza, non si vedeva niente.
   */
  const error = useArenaStore((s) => s.error);
  const setError = useArenaStore((s) => s.setError);
  const lastGameResult = useArenaStore((s) => s.lastGameResult);
  const matchFoundData = useArenaStore((s) => s.matchFoundData);
  const hasAcknowledgedMatch = useArenaStore((s) => s.hasAcknowledgedMatch);
  const clockState = useArenaStore((s) => s.clockState);
  const clockTimeControl = useArenaStore((s) => s.timeControl);
  const isOpponentDisconnected = useArenaStore((s) => s.isOpponentDisconnected);
  const primaMossaScadeAt = useArenaStore((s) => s.primaMossaScadeAt);
  const primaMossaEsito = useArenaStore((s) => s.primaMossaEsito);
  const openingPlyCount = useArenaStore((s) => s.openingPlyCount);
  const graceTimeRemaining = useArenaStore((s) => s.graceTimeRemaining);
  const opponentMaiArrivato = useArenaStore((s) => s.opponentMaiArrivato);
  const opponentDecideLOrologio = useArenaStore((s) => s.opponentDecideLOrologio);
  /* L'apertura sorteggiata: in arena il cartellino sopra la scacchiera è
     l'unico posto in cui viene detta. L'overlay «partita trovata», dove la
     scrivono torneo e sfida, qui non compare mai (`setArenaMatch`
     auto-conferma). */
  const drawnOpening = useArenaStore((s) => s.drawnOpening);
  const manche = useArenaStore((s) => s.manche);
  const mancheTotali = useArenaStore((s) => s.mancheTotali);
  const drawOffered = useArenaStore((s) => s.drawOffered);
  const opponentOfferedDraw = useArenaStore((s) => s.opponentOfferedDraw);
  const drawDeclinedNotice = useArenaStore((s) => s.drawDeclinedNotice);
  /* Il freno alle proposte di patta. Selettori reattivi: i test dei componenti
     sostituiscono lo store con un finto che non ha `.getState`. */
  const drawOffersLeft = useArenaStore((s) => s.drawOffersLeft);
  const drawMustMove = useArenaStore((s) => s.drawMustMove);
  const pattaBloccata = etichettePattaBloccata(drawOffersLeft, drawMustMove);
  // Le azioni sono riferimenti stabili: selezionarle una per una non costa nulla.
  const mossaInVolo = useArenaStore((s) => s.preMoveBoardState);
  const resyncPartita = useMultiplayerStore((s) => s.resyncGame);
  const makeMove = useArenaStore((s) => s.makeMove);
  const resign = useArenaStore((s) => s.resign);
  const resumeSearch = useArenaStore((s) => s.resumeSearch);
  /* L'andata è finita e il ritorno è già in viaggio: l'overlay lo dice e
     sparecchia i bottoni. Vedi `setRitornoPending` nello store. */
  const ritornoPending = useArenaStore((s) => s.ritornoPending);
  const pauseSearch = useArenaStore((s) => s.pauseSearch);
  const confirmMatch = useArenaStore((s) => s.confirmMatch);
  const tickClock = useArenaStore((s) => s.tickClock);
  /* Il boost prende il posto di Focus finché c'è: la scelta va fatta qui,
     perché è qui che si monta la barra. Vedi `useCanBoost`. */
  const canBoost = useCanBoost();
  /* Il boost preso resta scritto accanto al proprio nome: vale fino
     all'ultima mossa, non solo nell'istante in cui lo si prende. */
  const boostActivated = useArenaStore((s) => s.boostActivated);
  const offerDraw = useArenaStore((s) => s.offerDraw);
  const acceptDraw = useArenaStore((s) => s.acceptDraw);
  const declineDrawAction = useArenaStore((s) => s.declineDraw);

  const currentUser = useAuthStore((state) => state.user);
  /*
   * L'arena in cui si GIOCA, non quella che si guarda: qui si descrive la
   * partita in corso, e le due divergono appena si apre un'altra arena
   * dall'elenco restando in coda. Vedi `useArenaPartita`.
   */
  const arenaPartita = useArenaPartita();
  /*
   * Il tempo dell'arena e' finito mentre si giocava: la partita appena chiusa
   * e' l'ultima, e il riquadro non deve offrire «Continua». Vedi
   * `useArenaScaduta`: si legge l'arena in cui si GIOCA, non quella che si
   * guarda, come tutto il resto di questa schermata.
   */
  const partitaInArenaScaduta = useArenaScaduta(arenaPartita);

  // Chat unread badge + active flag for the mobile full-screen chat overlay.
  const unreadCount = useChatStore((state) => state.unreadCount);
  const { count: spectatorCount } = useGameSpectators();
  const setChatActive = useChatStore((state) => state.setActive);

  // Sync to local game store for board rendering
  const syncToLocalStore = useGameStore((state) => state.syncFromExternal);

  const currentTurn = boardState?.currentTurn ?? Color.WHITE;
  const gameStatus = boardState?.status ?? GameStatus.PLAYING;

  const isOurTurn =
    (playerColor === 'white' && currentTurn === Color.WHITE) ||
    (playerColor === 'black' && currentTurn === Color.BLACK);

  const isGameOver = gameStatus !== GameStatus.PLAYING || gameResult !== null;

  /* La stessa sentinella dell'online: una damiera ferma in stato `'playing'`
     non sembra un guasto, e senza qualcuno che se ne accorga non si esce piu'.
     Il resync passa dal multiplayer-store, che e' l'unica porta di rientro e
     delega a questo store il risultato. */
  useGuardiaSincronia({
    attiva: status === 'playing' && !isGameOver,
    turnoDamiera: boardState ? (boardState.currentTurn === Color.WHITE ? 'white' : 'black') : null,
    turnoOrologio: clockState?.activePlayer ?? null,
    mossaInVolo: mossaInVolo != null,
    resync: resyncPartita,
    contesto: 'arena',
  });

  // Auto-dismiss della pastiglia "l'avversario ha rifiutato la patta". Stessa
  // durata e stessa ragione della partita online (vedi OnlineGame).
  useEffect(() => {
    if (!drawDeclinedNotice) return;
    const id = window.setTimeout(
      () => useArenaStore.setState({ drawDeclinedNotice: false }),
      DRAW_DECLINE_NOTICE_MS,
    );
    return () => window.clearTimeout(id);
  }, [drawDeclinedNotice]);

  // Debug logging for arena game state transitions
  useEffect(() => {
    log.info('ArenaGame', 'State changed', { status, playerColor, isGameOver, hasMatch: !!matchFoundData });
  }, [status, playerColor, boardState, gameResult, isGameOver, matchFoundData]);

  /*
   * L'azione si legge con un SELETTORE, non con `useGameStore.getState()`.
   *
   * I test di questi tre wrapper montano un finto store che è solo un selettore
   * (`useGameStore(selector)`), senza `.getState`: chiamarlo qui fa cadere il
   * componente in prova — cioè un difetto che si scopre solo dopo. È una lezione
   * già pagata in questo repo.
   */
  const enterOnlineGame = useGameStore((s) => s.enterOnlineGame);

  // Arena play is player-vs-player: reset the shared game store to 'pvp' so a
  // leftover AI mode from an earlier local game (gameMode is never reset when
  // switching AI → online) can't block review navigation or suppress the go-live
  // blink. Board state itself comes from the server via syncFromExternal.
  useEffect(() => {
    enterOnlineGame();
  }, []);

  // Sync board state when it changes
  useEffect(() => {
    if (boardState && syncToLocalStore) {
      syncToLocalStore(boardState);
    }
  }, [boardState, syncToLocalStore]);

  // Handle move from board
  const handleMove = useCallback(
    (move: Move) => {
      if (isOurTurn && !isGameOver) {
        makeMove(move);
      }
    },
    [isOurTurn, isGameOver, makeMove]
  );

  // Turn text
  const getTurnText = (): string => {
    if (isGameOver) {
      /*
       * Stessa guardia di `OnlineGame.getTurnText`, e per la stessa ragione:
       * `isGameOver` è vero appena `boardState.status` è terminale, cioè col
       * `game:update` che precede `game:end` di una transazione intera. Qui
       * `getResultClass` la guardia ce l'aveva già (`if (!gameResult) return ''`)
       * e il TESTO no: il vincitore leggeva «Hai Perso!» in nero invece che in
       * rosso — meno vistoso, non meno sbagliato.
       */
      if (!gameResult) return t('game.result.gameOver');
      const isWin = gameResult === playerColor;
      const isDraw = gameResult === 'draw';
      if (isDraw) return t('game.result.draw');
      if (isWin) return t('game.result.youWin');
      return t('game.result.youLose');
    }
    if (isOurTurn) return t('game.turn.yourTurn');
    return t('game.turn.opponentTurn');
  };

  const getResultClass = (): string => {
    if (!gameResult) return '';
    if (gameResult === playerColor) return 'win';
    if (gameResult === 'draw') return 'draw';
    return 'loss';
  };

  /*
   * «Continua»: si torna online. La pausa scritta a fine partita dal server la
   * toglie `arena:resume`, e da qui in poi si viene di nuovo accoppiati.
   *
   * L'esito non si buttava via per scelta: `resumeSearch` col socket giu'
   * scriveva in `error` dello store, che qui nessuno legge — a schermo c'e'
   * `ArenaGame`, non la pagina dell'arena — e il riquadro restava identico.
   * Il bottone si poteva premere all'infinito senza che succedesse niente e
   * senza che venisse detto perche'.
   */
  const [ripresaFallita, setRipresaFallita] = useState(false);
  const handlePlayNext = useCallback(() => {
    setRipresaFallita(!resumeSearch());
  }, [resumeSearch]);

  /* Un esito per partita: il riquadro della prossima non nasce con l'avviso
     di quella prima. */
  useEffect(() => {
    setRipresaFallita(false);
  }, [lastGameResult]);

  /*
   * Rilasciata dal rientro: si esce dalla schermata.
   *
   * `rilasciaPartita` azzera la damiera quando il server, al ritorno in primo
   * piano, non conosce piu' la partita. Restare qui vorrebbe dire fissare una
   * damiera che non e' di nessuna partita — e' la stessa uscita che il
   * multiplayer ha in `App.tsx` con `laPartitaOnlineEStataRilasciata`.
   * `'lobby'` non e' uno stato in cui questa schermata possa nascere: ci si
   * arriva solo da 'matchFound'/'playing'.
   */
  useEffect(() => {
    if (status === 'lobby' && !boardState) onBackToLobby();
  }, [status, boardState, onBackToLobby]);

  /*
   * «In pausa»: si resta fermi e si va a vedere la classifica.
   *
   * A questo punto il server ha gia' scritto la pausa (`chiudiFinePartita`), e
   * `pauseSearch` la ripete: e' idempotente, ed e' la rete di sicurezza se
   * `handleArenaGameEnd` e' fallita — il suo `catch` lascia partire `game:end`
   * lo stesso. Serve comunque per il lavoro che fa solo qui: chiude la
   * conversazione, butta un eventuale accoppiamento in attesa e porta lo stato
   * a 'lobby'.
   */
  const handlePause = useCallback(() => {
    pauseSearch();
    onBackToLobby();
  }, [pauseSearch, onBackToLobby]);

  /*
   * LA CLASSIFICA DURANTE LA PARTITA.
   *
   * Si apre toccando il punteggio dell'intestazione, che c'era gia' e diceva
   * gia' «posto · punti»: e' il posto dove la domanda «come sto andando?» ha
   * gia' mezza risposta, e in arena quella domanda si fa piu' spesso che in un
   * torneo — le partite durano pochi minuti e la classifica si muove a ogni
   * partita chiusa di chiunque. Non e' un quarto bottone nella barra in basso
   * perche' a 360px la barra e' piena: Mosse, tre frecce, Chat, occhio.
   *
   * Su mobile apre il pannello a tutto schermo (lo stesso di Mosse e Chat); su
   * desktop seleziona la scheda con la coppa nella colonna laterale, che c'e'
   * comunque e si puo' aprire anche da li'. E' la strada che il torneo ha gia'
   * fatto, vedi `TournamentGame`.
   *
   * L'elemento e' creato UNA volta: la classifica legge lo store da se', e un
   * elemento stabile non fa ridisegnare la colonna a ogni tick dell'orologio.
   */
  const isMobile = useIsMobile();
  const classifica = useMemo(() => <ClassificaArenaInPartita />, []);
  const [classificaAperta, setClassificaAperta] = useState(false);
  const [richiestaClassifica, setRichiestaClassifica] = useState(0);
  const apriClassifica = useCallback(() => {
    if (isMobile) setClassificaAperta(true);
    else setRichiestaClassifica((n) => n + 1);
  }, [isMobile]);
  // Da mobile a desktop col pannello aperto (rotazione, finestra allargata):
  // il pannello si chiude, e al ritorno su mobile non si riapre da solo.
  useEffect(() => {
    if (!isMobile) setClassificaAperta(false);
  }, [isMobile]);

  // Build match found data for GameScreen overlay
  const matchOverlay: MatchFoundData | null =
    status === 'matchFound' && matchFoundData && !hasAcknowledgedMatch
      ? {
          opponentUsername: matchFoundData.opponentUsername,
          opponentRating: matchFoundData.opponentRating,
          opponentAvatar: matchFoundData.opponentAvatar,
          assignedColor: matchFoundData.color,
        }
      : null;

  // Player info — topPlayer is always the opponent, bottomPlayer is always us
  // (board flip is handled by the `flipped` prop)
  const color = playerColor ?? 'white';
  // `||` e non `??`: vedi la nota gemella in `TournamentGame`.
  const topPlayer = useMemo(() => ({ username: opponent?.username || t('common.anonymous'), rating: opponent?.rating, avatar: opponent?.avatar ?? null, profileUsername: opponent?.username ?? null }), [opponent, t]);
  const bottomPlayer = useMemo(() => ({ username: currentUser?.username ?? t('common.you'), rating: getRatingForGame(currentUser, arenaPartita?.variant as string ?? '', arenaPartita?.timeControl ?? ''), avatar: currentUser?.avatar ?? null, country: currentUser?.country ?? null, profileUsername: currentUser?.username ?? null, nameBadge: boostActivated ? <ArenaBoostBadge /> : undefined }), [currentUser, t, arenaPartita?.variant, arenaPartita?.timeControl, boostActivated]);

  // The arena board lives in the arena store, so the move list has to be fed
  // explicitly — left to itself it would read the (empty) multiplayer store.
  // The variant comes from the board the server sent: an Italian arena must be
  // written in Italian notation. currentArena covers the gap before game:start.
  const moveHistory = boardState?.moveHistory;
  const notationVariant = boardState?.variant ?? arenaPartita?.variant;

  const timeControlLabel = !arenaPartita?.timeControl
    ? undefined
    : arenaPartita.timeControl === 'none'
      ? t('game.timeControls.noTimeLimit')
      : resolveTimeControl(arenaPartita.timeControl)?.name;

  const mobilePanels: MobilePanel[] = useMemo(() => [
    {
      id: 'moves',
      label: t('game.sidebar.moves'),
      icon: <List size={20} aria-hidden="true" />,
      render: () => <MoveList moves={moveHistory} variant={notationVariant} opening={drawnOpening} />,
    },
    {
      id: 'chat',
      label: t('game.sidebar.chat'),
      icon: <MessageSquare size={20} aria-hidden="true" />,
      badge: unreadCount,
      onOpen: () => setChatActive(true),
      onClose: () => setChatActive(false),
      render: () => <ChatPanel />,
    },
    /* L'occhio sta a destra della Chat, e c'è anche quando non guarda nessuno:
       sapere di NON essere guardati è un'informazione quanto il contrario. Di
       sola icona perché a 360px la barra non regge una terza parola. */
    {
      id: 'spectators',
      label: t('game.sidebar.spectators'),
      icon: <Eye size={20} aria-hidden="true" />,
      badge: spectatorCount,
      compact: true,
      render: () => <SpectatorsPanel />,
    },
  ], [t, moveHistory, notationVariant, drawnOpening, unreadCount, spectatorCount, setChatActive]);

  /* Memoizzati: scendono a `GameScreen`, memoizzato, e questo componente si
     ri-renderizza a ogni tick dell'orologio (in arena anche a ogni classifica,
     che arriva ogni due secondi). Stessa cura di `OnlineGame`. */
  const focusSlot = useMemo(() => (canBoost ? <ArenaBoostButton /> : undefined), [canBoost]);

  const boardOverlaySlot = useMemo(() => (
        <>
          <DrawnOpeningBanner
            opening={drawnOpening}
            triggerKey={null}
            /* Accanto all'«apertura sorteggiata» basta «1/2»; da solo — arena a
               mossa libera, dove il banner non ha altro da dire — quel numero
               non si capirebbe, e serve la frase intera. Stessa scelta di
               `OnlineGame` e `TournamentGame`, stesse chiavi. */
            legLabel={
              manche && mancheTotali
                ? t(drawnOpening ? 'online.match.legShort' : 'online.match.legOf',
                    { leg: manche, total: mancheTotali })
                : undefined
            }
          />
          {/*
            L'errore di partita, che qui non si vedeva.
            `AvvisoErrore` era montato SOLO in `OnlineGame` (grep: tre riscontri in
            tutto `src`, la definizione e quell'unico montaggio): chi giocava in
            arena o in torneo non leggeva nulla di quello che lo store scriveva in
            `error` — nemmeno il riavvio del server, che arriva a tutta la stanza.
          */}
          <AvvisoErrore errore={error} onChiudi={() => setError(null)} />
        </>
  ), [drawnOpening, manche, mancheTotali, t, error, setError]);

  const overlaySlot = useMemo(() => (
        status === 'gameEnd' && lastGameResult ? (
          <ArenaGameEndOverlay
            result={lastGameResult}
            reasonText={endReasonText(t, gameEndReason, {
              loser: gameResult === 'white' ? Color.BLACK : gameResult === 'black' ? Color.WHITE : undefined,
              ruleMode: boardState?.ruleMode,
            })}
            arenaStatus={arenaPartita?.status === 'finished' ? 'finished' : partitaInArenaScaduta ? 'draining' : 'active'}
            onPlayNext={handlePlayNext}
            playNextErrorKey={ripresaFallita ? 'arena.gameEnd.resumeFailed' : null}
            onPause={handlePause}
            ritornoStartsAt={ritornoPending?.startsAt ?? null}
          />
        ) : undefined
  ), [status, lastGameResult, t, gameEndReason, gameResult, boardState?.ruleMode,
      arenaPartita?.status, partitaInArenaScaduta, handlePlayNext, ripresaFallita,
      handlePause, ritornoPending?.startsAt]);

  const sidebarSlot = useMemo(() => (
        <GameSidebarTabs
          moves={moveHistory}
          variant={notationVariant}
          opening={drawnOpening}
          openingPlyCount={openingPlyCount}
          standings={classifica}
          standingsRequest={richiestaClassifica}
        />
  ), [moveHistory, notationVariant, drawnOpening, openingPlyCount, classifica, richiestaClassifica]);

  return (
    <GameScreen
      boardState={boardState}
      playerColor={color}
      isMyTurn={isOurTurn}
      isGameOver={isGameOver}
      flipped={color === 'black'}
      onMove={handleMove}
      allowPremove
      clockState={clockState}
      timeControlPreset={clockTimeControl}
      isPlaying={status === 'playing'}
      boardInteractive={status === 'playing'}
      tickClock={tickClock}
      topPlayer={topPlayer}
      bottomPlayer={bottomPlayer}
      turnText={getTurnText()}
      resultClass={getResultClass()}
      isOpponentDisconnected={isOpponentDisconnected}
      graceTimeRemaining={graceTimeRemaining}
      opponentMaiArrivato={opponentMaiArrivato}
      opponentDecideLOrologio={opponentDecideLOrologio}
      primaMossaScadeAt={primaMossaScadeAt}
      primaMossaEsito={primaMossaEsito}
      openingPlyCount={openingPlyCount}
      matchFoundData={matchOverlay}
      onMatchCountdownEnd={confirmMatch}
      headerCells={
        <>
          <ArenaHeaderCells onApriClassifica={apriClassifica} />
          {/* Il pannello sta QUI e non in un altro slot: e' un portale su
              `document.body`, quindi la sua posizione nell'albero non mette
              niente dentro la riga, e sta accanto al bottone che lo apre.
              `GameScreen` rende `headerCells` una volta sola — damiera sotto i
              900px, testa della colonna sopra — quindi non ci sono due
              portali. Su desktop non si monta affatto. */}
          {classificaAperta && isMobile && (
            <FullscreenPanel
              title={t('tournament.standings.title')}
              closeLabel={t('common.close')}
              onClose={() => setClassificaAperta(false)}
            >
              {classifica}
            </FullscreenPanel>
          )}
        </>
      }
      focusSlot={focusSlot}
      /*
       * `triggerKey={null}`: il cartellino si riarma su un `triggerKey` nuovo
       * OPPURE su un'apertura nuova, e in arena la seconda basta. Ogni
       * `game:start` costruisce un oggetto nuovo in `setGameStart`, quindi il
       * riferimento cambia a ogni partita anche quando `ArenaGame` resta
       * montato fra una partita e l'altra — che è il caso che conta, il
       * "Gioca la prossima". Un numero in più nello store esisterebbe solo per
       * riempire questo prop. Se un domani lo store riusasse lo stesso oggetto
       * fra due partite, il cartellino della seconda non si riarmerebbe.
       */
      boardOverlaySlot={boardOverlaySlot}
      overlaySlot={overlaySlot}
      sidebarSlot={sidebarSlot}
      mobilePanels={mobilePanels}
      variant={notationVariant ? (notationVariant.toLowerCase() as 'international' | 'italian' | 'english') : undefined}
      timeControlLabel={timeControlLabel}
      isRated={arenaPartita?.isRated}
      ruleMode={boardState?.ruleMode}
      onResign={resign}
      onOfferDraw={offerDraw}
      onAcceptDraw={acceptDraw}
      onDeclineDraw={declineDrawAction}
      drawOffered={drawOffered}
      opponentOfferedDraw={opponentOfferedDraw}
      /*
       * La pastiglia dice solo il RIFIUTO, come prima: cosa fare lo dice il
       * bottone, che e' due centimetri sotto e nello stesso sguardo («Muovi
       * prima»). Concatenare le due frasi dava una pastiglia di cinque righe a
       * 360px che ripeteva «patta» tre volte — e un trattino che incolla due
       * frasi e' una traduzione che non regge in otto lingue.
       */
      offerNotice={drawDeclinedNotice ? t('game.actions.drawDeclined') : null}
      drawBlockedLabel={pattaBloccata ? t(pattaBloccata.label) : null}
      drawBlockedReason={pattaBloccata ? t(pattaBloccata.reason) : null}
    />
  );
}
